import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CountrySelect } from '@/components/country-select'
import { SettingsRow } from '@/components/settings/settings-row'
import type { EmployerPostingPreferences } from '@/lib/employer-posting-preferences'
import { EMPLOYMENT_TYPE_LABELS, WORK_MODE_LABELS } from '@/utils/job-meta-labels'

type EmployerPostingPreferencesFieldsProps = {
  prefs: EmployerPostingPreferences
  onChange: (prefs: EmployerPostingPreferences) => void
  notifyEmailEnabled: boolean
  onNotifyEmailChange: (enabled: boolean) => void
  disabled?: boolean
  layout: 'onboarding' | 'settings'
}

const controlClassName = 'h-8 w-full text-sm sm:max-w-[320px]'

export function EmployerPostingPreferencesFields({
  prefs,
  onChange,
  notifyEmailEnabled,
  onNotifyEmailChange,
  disabled = false,
  layout,
}: EmployerPostingPreferencesFieldsProps) {
  const showCity = prefs.default_work_mode === 'onsite' || prefs.default_work_mode === 'hybrid'

  const employmentTypeSelect = (
    <Select
      value={prefs.default_employment_type || '__none__'}
      onValueChange={(value) =>
        onChange({
          ...prefs,
          default_employment_type: value === '__none__' ? '' : value,
        })
      }
      disabled={disabled}
    >
      <SelectTrigger id="default-employment-type" className={layout === 'settings' ? controlClassName : undefined}>
        <SelectValue placeholder="Choose a default" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">No default</SelectItem>
        {Object.entries(EMPLOYMENT_TYPE_LABELS).map(([value, label]) => (
          <SelectItem key={value} value={value}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const workModeSelect = (
    <Select
      value={prefs.default_work_mode || '__none__'}
      onValueChange={(value) =>
        onChange({
          ...prefs,
          default_work_mode: value === '__none__' ? '' : value,
          default_city: value === 'remote' ? '' : prefs.default_city,
        })
      }
      disabled={disabled}
    >
      <SelectTrigger id="default-work-mode" className={layout === 'settings' ? controlClassName : undefined}>
        <SelectValue placeholder="Choose a default" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">No default</SelectItem>
        {Object.entries(WORK_MODE_LABELS).map(([value, label]) => (
          <SelectItem key={value} value={value}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const cityInput = (
    <Input
      id="default-city"
      value={prefs.default_city}
      onChange={(e) => onChange({ ...prefs, default_city: e.target.value })}
      placeholder="e.g. Melbourne"
      disabled={disabled}
      className={layout === 'settings' ? controlClassName : undefined}
    />
  )

  const countrySelect = (
    <CountrySelect
      id="default-country"
      value={prefs.default_country_code}
      onValueChange={(code) => onChange({ ...prefs, default_country_code: code })}
      disabled={disabled}
      className={layout === 'settings' ? controlClassName : undefined}
    />
  )

  const salaryVisibilitySelect = (
    <Select
      value={prefs.default_salary_visibility}
      onValueChange={(value: 'shown' | 'not_disclosed') =>
        onChange({ ...prefs, default_salary_visibility: value })
      }
      disabled={disabled}
    >
      <SelectTrigger id="default-salary-visibility" className={layout === 'settings' ? controlClassName : undefined}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="not_disclosed">Prefer not to disclose by default</SelectItem>
        <SelectItem value="shown">Show salary range by default</SelectItem>
      </SelectContent>
    </Select>
  )

  const notifySwitch = (
    <Switch
      id="notify-applications"
      checked={notifyEmailEnabled}
      onCheckedChange={onNotifyEmailChange}
      disabled={disabled}
    />
  )

  if (layout === 'settings') {
    return (
      <>
        <SettingsRow label="Default employment type" htmlFor="default-employment-type">
          {employmentTypeSelect}
        </SettingsRow>
        <SettingsRow label="Default work arrangement" htmlFor="default-work-mode">
          {workModeSelect}
        </SettingsRow>
        {showCity ? (
          <SettingsRow
            label="Default city"
            htmlFor="default-city"
            description="Shown when work arrangement is on-site or hybrid."
          >
            {cityInput}
          </SettingsRow>
        ) : null}
        <SettingsRow label="Default country" htmlFor="default-country">
          {countrySelect}
        </SettingsRow>
        <SettingsRow label="Salary on new postings" htmlFor="default-salary-visibility">
          {salaryVisibilitySelect}
        </SettingsRow>
        <SettingsRow
          label="Email on new applications"
          htmlFor="notify-applications"
          description="Application alerts for roles you manage. Applies to your account. In-app alerts are in Settings → Notifications."
          showDivider={false}
        >
          {notifySwitch}
        </SettingsRow>
      </>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="default-employment-type">Default employment type</Label>
          {employmentTypeSelect}
        </div>
        <div className="space-y-2">
          <Label htmlFor="default-work-mode">Default work arrangement</Label>
          {workModeSelect}
        </div>
      </div>

      {showCity ? (
        <div className="space-y-2">
          <Label htmlFor="default-city">Default city</Label>
          {cityInput}
        </div>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="default-country">Default country</Label>
        {countrySelect}
      </div>

      <div className="space-y-2">
        <Label htmlFor="default-salary-visibility">Salary on new job postings</Label>
        {salaryVisibilitySelect}
      </div>

      <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
        <div className="space-y-1">
          <Label htmlFor="notify-applications" className="font-normal leading-snug">
            Email me when someone applies to a job
          </Label>
          <p className="text-xs text-muted-foreground">
            Application alerts for roles you manage. Applies to your account.
          </p>
        </div>
        {notifySwitch}
      </div>
    </div>
  )
}
